import { CANVAS_WIDTH } from "../../game/constants";
import { clamp01, easeOutBack, easeOutCubic } from "../../shared/easing";
import type { Rect, UiRects } from "../../game/input";
import type { RenderModel } from "../renderModel";
import { getCharacterById } from "../../economy/characters";
import { rankedTierForRating, type RankedTier } from "../../versus/ranked";
import {
  GAME_FONT,
  mFont,
  menuButtonColumns,
  menuBtnH,
  roundedRectPath,
  formatTime,
} from "../renderConstants";
import type { drawButton } from "../ui";

// ── Types ────────────────────────────────────────────────────────────────

export interface VersusResultAnimState {
  versusRatingChangeBefore: number;
  versusRatingChangeMs: number;
  versusRemoteLeftMs: number;
}

export interface VersusResultDeps {
  ctx: CanvasRenderingContext2D;
  canvasHeight: number;
  nowMs: () => number;
  nowSec: () => number;
  rankVinylImages: Partial<Record<RankedTier, HTMLImageElement>>;
  /** Mutable animation state — passed by reference (object). */
  animState: VersusResultAnimState;
  /** Helper callbacks forwarded from the renderer class. */
  drawBtn: (rect: Rect, label: string, options?: Parameters<typeof drawButton>[3]) => void;
  drawCharacterPortrait: (path: string, x: number, y: number, w: number, h: number) => void;
  drawGemIcon: (x: number, y: number, size: number) => void;
}

// ── Extracted screen function ────────────────────────────────────────────

export function drawVersusResultScreen(deps: VersusResultDeps, model: RenderModel): UiRects {
  const ctx = deps.ctx;
  const centerX = CANVAS_WIDTH / 2;
  const menuCols = menuButtonColumns(centerX);
  const vr = model.versusResult;
  const canvasH = deps.canvasHeight;

  // ── Both characters in background (like countdown screen) ──
  if (vr != null) {
    const localChar = getCharacterById(vr.localSkin) ?? model.equippedCharacter;
    const remoteChar = getCharacterById(vr.remoteSkin) ?? model.equippedCharacter;
    const bigSize = CANVAS_WIDTH * 1.2;
    const timeSec = deps.nowSec();
    const bobAmp = 3;
    const bobL = Math.sin(timeSec * 1.2) * bobAmp;
    const bobR = Math.sin(timeSec * 1.2 + Math.PI) * bobAmp;

    // Local player — left, bobbing
    const localX = -bigSize / 2 + 35;
    const localY = canvasH - bigSize * 1.0 + bobL;
    deps.drawCharacterPortrait(localChar.imagePath, localX, localY, bigSize, bigSize);

    // Opponent — right, mirrored, bobbing counter-phase, slides off when they leave
    if (model.rematchRemoteLeft && deps.animState.versusRemoteLeftMs === 0) {
      deps.animState.versusRemoteLeftMs = deps.nowMs();
    }
    if (!model.rematchRemoteLeft) {
      deps.animState.versusRemoteLeftMs = 0;
    }
    const slideOffT = deps.animState.versusRemoteLeftMs > 0
      ? easeOutCubic(clamp01((deps.nowMs() - deps.animState.versusRemoteLeftMs) / 800))
      : 0;
    const opSlideX = slideOffT * (CANVAS_WIDTH * 0.8);

    const opX = CANVAS_WIDTH - (-bigSize / 2 + 35) - bigSize + opSlideX;
    const opY = canvasH - bigSize * 1.0 + bobR;
    if (slideOffT < 1) {
      ctx.save();
      ctx.globalAlpha = 1 - slideOffT;
      ctx.translate(CANVAS_WIDTH + opSlideX, 0);
      ctx.scale(-1, 1);
      deps.drawCharacterPortrait(remoteChar.imagePath, CANVAS_WIDTH - opX + opSlideX - bigSize, opY, bigSize, bigSize);
      ctx.restore();
    }
  }

  // Dark overlay (drawn over characters — full canvas coverage)
  ctx.fillStyle = "rgba(10, 5, 15, 0.4)";
  ctx.fillRect(-10, -10, CANVAS_WIDTH + 20, canvasH + 20);

  if (vr == null) {
    const mainMenu: Rect = { x: menuCols.fullX, y: canvasH - 70, w: menuCols.fullW, h: menuBtnH() };
    deps.drawBtn(mainMenu, "MAIN MENU", { variant: "lobby", fill: "rgba(20,36,54,0.85)", stroke: "rgba(108,200,255,0.6)", font: mFont("bold", 5) });
    return { mainMenu };
  }

  const step = model.versusResultStep;
  const stepT = model.versusResultStepElapsed;

  // Lobby-style button helper
  const drawLobbyBtn = (rect: Rect, label: string, tint?: string): void => {
    roundedRectPath(ctx, rect.x, rect.y, rect.w, rect.h, 6);
    ctx.fillStyle = tint ?? "rgba(20,36,54,0.85)";
    ctx.fill();
    ctx.strokeStyle = "rgba(108,200,255,0.6)";
    ctx.lineWidth = 1.5;
    ctx.stroke();
    ctx.fillStyle = "#e9f7ff";
    ctx.font = mFont("bold", 6);
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(label, rect.x + rect.w / 2, rect.y + rect.h / 2 + 1);
  };

  // Shared: WIN / LOSE / DRAW title (all steps)
  {
    let titleText: string;
    let titleColor: string;
    if (vr.draw) { titleText = "DRAW"; titleColor = "#ffd700"; }
    else if (vr.localWon) { titleText = "VICTORY!"; titleColor = "#44ff88"; }
    else { titleText = "DEFEAT"; titleColor = "#ff4444"; }

    const titleAlpha = step === 0 ? clamp01(stepT / 0.4) : 1;
    const slideY = step === 0 ? -20 * (1 - easeOutCubic(clamp01(stepT / 0.5))) : 0;
    ctx.globalAlpha = titleAlpha;
    ctx.font = `bold 16px ${GAME_FONT}`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = titleColor;
    ctx.fillText(titleText, centerX, 70 + slideY);
    ctx.globalAlpha = 1;

  }

  const contentTop = 110;
  const nextBtnY = canvasH - 70;
  const contentMidY = (contentTop + nextBtnY) / 2;

  // Tier thresholds for circular MMR bar progress
  const TIER_FLOORS: Record<RankedTier, number> = { Regular: 0, Silver: 250, Gold: 500, Diamond: 750 };
  const TIER_CAPS: Record<RankedTier, number> = { Regular: 250, Silver: 500, Gold: 750, Diamond: 1250 };

  // ===================== STEP 0 — RANKING =====================
  if (step === 0) {
    const ratingBefore = vr.ratingBefore;
    const ratingAfter = model.versusRatingAfter;
    const tierBefore = vr.tierBefore;

    // Detect when rating change first arrives from DB
    const ratingChanged = ratingAfter != null && ratingAfter !== ratingBefore;
    if (ratingChanged && deps.animState.versusRatingChangeMs === 0) {
      deps.animState.versusRatingChangeMs = deps.nowMs();
      deps.animState.versusRatingChangeBefore = ratingBefore;
    }
    // Reset tracker if we're on a fresh result (step just started)
    if (stepT < 0.1) {
      deps.animState.versusRatingChangeMs = 0;
      deps.animState.versusRatingChangeBefore = 0;
    }

    // Animated MMR count-up — starts when rating change is detected
    const mmrAnimDur = 1.2;
    let mmrT = 0;
    if (deps.animState.versusRatingChangeMs > 0) {
      const secSinceDetected = (deps.nowMs() - deps.animState.versusRatingChangeMs) / 1000;
      mmrT = easeOutCubic(clamp01(secSinceDetected / mmrAnimDur));
    }
    const displayRating = ratingChanged
      ? Math.round(ratingBefore + (ratingAfter! - ratingBefore) * mmrT)
      : ratingBefore;
    const displayTier = rankedTierForRating(displayRating);

    // Determine if rank changed
    const tierAfter = ratingAfter != null ? rankedTierForRating(ratingAfter) : tierBefore;
    const rankUp = tierAfter !== tierBefore && TIER_FLOORS[tierAfter] > TIER_FLOORS[tierBefore];
    const rankUpTriggered = rankUp && mmrT >= 0.95;

    // Vinyl + circular progress ring
    const circleR = 48;
    const circleCy = contentMidY - 30;
    const ringWidth = 7;
    const scaleInT = clamp01(stepT / 0.5);
    const circleScale = easeOutBack(scaleInT);
    ctx.globalAlpha = clamp01(stepT / 0.4);

    // Compute progress within current tier
    const floor = TIER_FLOORS[displayTier];
    const cap = TIER_CAPS[displayTier];
    const tierProgress = clamp01((displayRating - floor) / (cap - floor));

    ctx.save();
    ctx.translate(centerX, circleCy);
    ctx.scale(circleScale, circleScale);

    // Background ring
    ctx.beginPath();
    ctx.arc(0, 0, circleR, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(30,50,80,0.8)";
    ctx.lineWidth = ringWidth;
    ctx.stroke();

    // Tier-colored progress ring
    const tierColors: Record<RankedTier, string> = {
      Regular: "#8eb6d9",
      Silver: "#c0d4e8",
      Gold: "#ffd700",
      Diamond: "#7cf0ff"
    };
    const ringColor = tierColors[displayTier];
    if (tierProgress > 0) {
      const startAngle = -Math.PI / 2;
      const endAngle = startAngle + Math.PI * 2 * tierProgress;
      ctx.beginPath();
      ctx.arc(0, 0, circleR, startAngle, endAngle);
      ctx.strokeStyle = ringColor;
      ctx.lineWidth = ringWidth;
      ctx.lineCap = "round";
      ctx.stroke();
      ctx.lineCap = "butt";

      // Glowing tip
      const tipX = Math.cos(endAngle) * circleR;
      const tipY = Math.sin(endAngle) * circleR;
      ctx.save();
      ctx.shadowColor = ringColor;
      ctx.shadowBlur = 10 + 6 * (0.5 + 0.5 * Math.sin(deps.nowMs() / 250));
      ctx.beginPath();
      ctx.arc(tipX, tipY, ringWidth * 0.7, 0, Math.PI * 2);
      ctx.fillStyle = "#ffffff";
      ctx.fill();
      ctx.restore();
    }

    // Inner circle with vinyl
    ctx.beginPath();
    ctx.arc(0, 0, circleR - ringWidth - 2, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(14,24,44,0.92)";
    ctx.fill();

    // Vinyl image (slowly rotating)
    const vinylImg = deps.rankVinylImages[displayTier] ?? null;
    const vinylSize = (circleR - ringWidth - 4) * 2;
    if (vinylImg != null) {
      ctx.save();
      ctx.rotate(deps.nowMs() / 3000);
      ctx.beginPath();
      ctx.arc(0, 0, circleR - ringWidth - 3, 0, Math.PI * 2);
      ctx.clip();
      ctx.drawImage(vinylImg, -vinylSize / 2, -vinylSize / 2, vinylSize, vinylSize);
      ctx.restore();
    }
    ctx.restore();

    // Tier name below vinyl
    if (stepT >= 0.5) {
      const tierTextT = clamp01((stepT - 0.5) / 0.4);
      ctx.globalAlpha = easeOutCubic(tierTextT);
      const slideY = 8 * (1 - easeOutCubic(tierTextT));
      ctx.font = mFont("bold", 8);
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillStyle = tierColors[displayTier];
      ctx.fillText(displayTier.toUpperCase(), centerX, circleCy + circleR + 12 + slideY);
    }

    // MMR number + delta below tier
    if (stepT >= 0.6) {
      const mmrTextT = clamp01((stepT - 0.6) / 0.4);
      ctx.globalAlpha = easeOutCubic(mmrTextT);
      const mmrSlideY = 10 * (1 - easeOutCubic(mmrTextT));
      const mmrY = circleCy + circleR + 30 + mmrSlideY;

      ctx.font = mFont("bold", 12);
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillStyle = "#e0e8f0";
      ctx.fillText(`${displayRating}`, centerX, mmrY);

      // Show delta once count-up is done
      if (ratingChanged && mmrT >= 0.9) {
        const diff = ratingAfter! - ratingBefore;
        const deltaAlpha = clamp01((mmrT - 0.9) / 0.1);
        ctx.globalAlpha = deltaAlpha;
        ctx.font = mFont("bold", 9);
        ctx.fillStyle = diff > 0 ? "#44ff88" : "#ff4444";
        ctx.fillText(`${diff > 0 ? "+" : ""}${diff}`, centerX, mmrY + 18);
      }
    }

    // Rank-up celebration
    if (rankUpTriggered && deps.animState.versusRatingChangeMs > 0) {
      // Rank-up starts after the MMR animation finishes
      const secSinceDetected = (deps.nowMs() - deps.animState.versusRatingChangeMs) / 1000;
      const rankUpT = clamp01((secSinceDetected - mmrAnimDur) / 0.6);
      if (rankUpT > 0) {
        const s = 0.5 + 1.5 * easeOutBack(rankUpT);
        ctx.globalAlpha = clamp01(rankUpT / 0.3);
        ctx.save();
        ctx.translate(centerX, circleCy + circleR + 68);
        ctx.scale(Math.min(s, 1), Math.min(s, 1));
        ctx.font = mFont("bold", 11);
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.shadowColor = tierColors[tierAfter];
        ctx.shadowBlur = 16;
        ctx.fillStyle = tierColors[tierAfter];
        ctx.fillText("RANK UP!", 0, 0);
        ctx.shadowBlur = 0;
        ctx.restore();

        // New tier name
        ctx.font = mFont("bold", 7);
        ctx.fillStyle = tierColors[tierAfter];
        ctx.fillText(tierAfter.toUpperCase(), centerX, circleCy + circleR + 84);
      }
    }
    ctx.globalAlpha = 1;

    const nextRect: Rect = { x: menuCols.fullX, y: nextBtnY, w: menuCols.fullW, h: menuBtnH() };
    const nextDelay = rankUp ? 2.8 : 1.5;
    const nextAlpha = clamp01((stepT - nextDelay) / 0.3);
    ctx.globalAlpha = nextAlpha;
    ctx.save();
    ctx.translate(0, 20 * (1 - easeOutCubic(clamp01((stepT - nextDelay) / 0.4))));
    drawLobbyBtn(nextRect, "NEXT");
    ctx.restore();
    ctx.globalAlpha = 1;
    return { versusResultNext: nextRect };
  }

  // ===================== STEP 1 — XP + Gems (side by side) =====================
  if (step === 1) {
    const rowCy = contentMidY - 20;
    const colSpacing = 60;
    const xpCx = centerX - colSpacing;
    const gemCx = centerX + colSpacing;

    // ── XP circle (left) ──
    const xpR = 28;
    const xpRingW = 5;
    const xpScaleT = clamp01(stepT / 0.4);
    const xpScale = easeOutBack(xpScaleT);
    ctx.globalAlpha = clamp01(stepT / 0.3);

    const xpFillT = clamp01((stepT - 0.3) / 0.8);
    const xpFillEased = easeOutCubic(xpFillT);
    const xpFrac = clamp01(model.xpBeforeRun + (model.xpProgress - model.xpBeforeRun) * xpFillEased);
    const displayLevel = model.levelUpTriggered && xpFillT >= 1.0 ? model.newLevel : model.level;

    ctx.save();
    ctx.translate(xpCx, rowCy);
    ctx.scale(xpScale, xpScale);
    ctx.beginPath();
    ctx.arc(0, 0, xpR, 0, Math.PI * 2);
    ctx.strokeStyle = "rgba(30,50,80,0.8)";
    ctx.lineWidth = xpRingW;
    ctx.stroke();
    if (xpFrac > 0) {
      const sa = -Math.PI / 2;
      const ea = sa + Math.PI * 2 * xpFrac;
      ctx.beginPath();
      ctx.arc(0, 0, xpR, sa, ea);
      ctx.strokeStyle = "#ffd700";
      ctx.lineWidth = xpRingW;
      ctx.lineCap = "round";
      ctx.stroke();
      ctx.lineCap = "butt";
    }
    ctx.beginPath();
    ctx.arc(0, 0, xpR - xpRingW - 1, 0, Math.PI * 2);
    ctx.fillStyle = "rgba(14,24,44,0.92)";
    ctx.fill();
    ctx.font = mFont("bold", 11);
    ctx.fillStyle = "#ffd700";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(`${displayLevel}`, 0, 1);
    ctx.restore();

    // XP text below circle
    if (stepT >= 0.5) {
      const xpTextT = clamp01((stepT - 0.5) / 0.3);
      ctx.globalAlpha = easeOutCubic(xpTextT);
      const xpNum = vr.xpGained ?? 0;
      ctx.font = mFont("bold", 7);
      ctx.fillStyle = "#ffd700";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText(xpNum > 0 ? `+${xpNum}` : "0", xpCx, rowCy + xpR + 8);
      ctx.font = mFont("bold", 5);
      ctx.fillStyle = "#7ca8ea";
      ctx.fillText("XP", xpCx, rowCy + xpR + 22);
    }

    // ── Gem icon (right) ──
    const gemDelay = 0.3;
    if (stepT >= gemDelay) {
      const gemT = clamp01((stepT - gemDelay) / 0.4);
      ctx.globalAlpha = clamp01((stepT - gemDelay) / 0.3);
      const gemSz = 34;
      const gemScale = easeOutBack(gemT);
      ctx.save();
      ctx.translate(gemCx, rowCy);
      ctx.scale(gemScale, gemScale);
      deps.drawGemIcon(-gemSz / 2, -gemSz / 2, gemSz);
      ctx.restore();

      if (stepT >= gemDelay + 0.3) {
        const gemTextT = clamp01((stepT - gemDelay - 0.3) / 0.4);
        ctx.globalAlpha = easeOutCubic(gemTextT);
        const gemCountEased = easeOutCubic(clamp01((stepT - gemDelay - 0.3) / 0.6));
        const displayGems = Math.floor(vr.gemsWon * gemCountEased);
        ctx.font = mFont("bold", 7);
        ctx.fillStyle = "#b565ff";
        ctx.textAlign = "center";
        ctx.textBaseline = "top";
        ctx.fillText(displayGems > 0 ? `+${displayGems}` : "0", gemCx, rowCy + xpR + 8);
        ctx.font = mFont("bold", 5);
        ctx.fillStyle = "#7ca8ea";
        ctx.fillText("GEMS", gemCx, rowCy + xpR + 22);
      }
    }
    ctx.globalAlpha = 1;

    const nextRect: Rect = { x: menuCols.fullX, y: nextBtnY, w: menuCols.fullW, h: menuBtnH() };
    const nextAlpha = clamp01((stepT - 1.2) / 0.3);
    ctx.globalAlpha = nextAlpha;
    ctx.save();
    ctx.translate(0, 20 * (1 - easeOutCubic(clamp01((stepT - 1.2) / 0.4))));
    drawLobbyBtn(nextRect, "NEXT");
    ctx.restore();
    ctx.globalAlpha = 1;
    return { versusResultNext: nextRect };
  }

  // ===================== STEP 2 — Ad → Heart Restored (only when out of hearts) =====================
  const needsAdStep = step === 2 && (model.staminaCurrent <= 0 || model.versusAdHeartRestored) && !model.staminaPremiumActive;
  if (needsAdStep) {
    if (!model.versusAdHeartRestored) {
      // Ad is showing — just dim screen
      return {};
    }
    // Heart restored — snappy animation then NEXT
    const heartSz = 50;
    const heartCy = contentMidY - 20;
    const scaleT = clamp01(stepT / 0.25);
    const heartScale = easeOutBack(scaleT);
    ctx.globalAlpha = clamp01(stepT / 0.15);

    ctx.save();
    ctx.translate(centerX, heartCy);
    ctx.scale(heartScale, heartScale);
    ctx.font = `${heartSz}px serif`;
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillStyle = "#ff4444";
    ctx.fillText("\u2764", 0, 0);
    ctx.restore();

    if (stepT >= 0.15) {
      ctx.globalAlpha = clamp01((stepT - 0.15) / 0.15);
      ctx.font = mFont("bold", 8);
      ctx.fillStyle = "#66ff88";
      ctx.textAlign = "center";
      ctx.textBaseline = "top";
      ctx.fillText("+1 LIFE", centerX, heartCy + heartSz / 2 + 12);
    }

    // NEXT button
    const adNextRect: Rect = { x: menuCols.fullX, y: nextBtnY, w: menuCols.fullW, h: menuBtnH() };
    if (stepT >= 0.35) {
      ctx.globalAlpha = clamp01((stepT - 0.35) / 0.15);
      ctx.save();
      ctx.translate(0, 10 * (1 - easeOutCubic(clamp01((stepT - 0.35) / 0.2))));
      drawLobbyBtn(adNextRect, "NEXT");
      ctx.restore();
    }
    ctx.globalAlpha = 1;
    return { versusResultNext: adNextRect };
  }

  // If ad step was used, final step is 3; otherwise 2
  const finalStep = model.versusAdHeartRestored ? 3 : 2;

  // ===================== FINAL STEP — Characters + Stats + Buttons =====================
  if (step < finalStep) return {};


  // Stats panel in center
  const panelDelay = 0.2;
  const panelH = 110;
  const panelY = contentMidY - panelH / 2;
  if (stepT >= panelDelay) {
    const panelT = clamp01((stepT - panelDelay) / 0.4);
    ctx.globalAlpha = easeOutCubic(panelT);
    ctx.save();
    ctx.translate(0, 15 * (1 - easeOutCubic(panelT)));

    const panelW = CANVAS_WIDTH - 24;
    const panelX = centerX - panelW / 2;
    roundedRectPath(ctx, panelX, panelY, panelW, panelH, 6);
    ctx.fillStyle = "rgba(20,36,54,0.85)";
    ctx.fill();
    roundedRectPath(ctx, panelX, panelY, panelW, panelH, 6);
    ctx.strokeStyle = "rgba(108,200,255,0.6)";
    ctx.lineWidth = 1.5;
    ctx.stroke();

    const colLeft = panelX + panelW * 0.25;
    const colRight = panelX + panelW * 0.75;
    const headerY = panelY + 10;
    ctx.font = mFont("bold", 6);
    ctx.textAlign = "center";
    ctx.textBaseline = "top";
    ctx.fillStyle = "#44ff88";
    ctx.fillText(vr.localDisplayName, colLeft, headerY);
    ctx.fillStyle = "#ff6b6b";
    ctx.fillText(vr.remoteDisplayName, colRight, headerY);
    ctx.fillStyle = "#5a7aaa";
    ctx.fillText("VS", centerX, headerY);

    ctx.strokeStyle = "rgba(108,200,255,0.25)";
    ctx.beginPath();
    ctx.moveTo(panelX + 8, headerY + 12);
    ctx.lineTo(panelX + panelW - 8, headerY + 12);
    ctx.stroke();

    const stats: Array<{ label: string; local: string; remote: string }> = [
      { label: "SCORE", local: String(vr.localScore), remote: String(vr.remoteScore) },
      { label: "LEVEL", local: String(vr.localLevel), remote: String(vr.remoteLevel) },
      { label: "TIME", local: formatTime(vr.elapsedSec), remote: formatTime(vr.elapsedSec) }
    ];
    for (let i = 0; i < stats.length; i++) {
      const y = headerY + 22 + i * 22;
      const stat = stats[i];
      ctx.font = mFont("bold", 5);
      ctx.textAlign = "center";
      ctx.fillStyle = "#5a7aaa";
      ctx.fillText(stat.label, centerX, y);
      ctx.font = mFont("bold", 7);
      ctx.fillStyle = (stat.label === "SCORE" ? vr.localScore > vr.remoteScore : stat.label === "LEVEL" ? vr.localLevel > vr.remoteLevel : false) ? "#ffd700" : "#e0e8f0";
      ctx.fillText(stat.local, colLeft, y);
      ctx.fillStyle = (stat.label === "SCORE" ? vr.remoteScore > vr.localScore : stat.label === "LEVEL" ? vr.remoteLevel > vr.localLevel : false) ? "#ffd700" : "#e0e8f0";
      ctx.fillText(stat.remote, colRight, y);
    }
    ctx.restore();
    ctx.globalAlpha = 1;
  }

  // Rematch indicators above character heads
  {
    const bigSize = CANVAS_WIDTH * 1.2;
    const timeSec = deps.nowSec();
    const bobAmp = 3;
    const bobL = Math.sin(timeSec * 1.2) * bobAmp;
    const bobR = Math.sin(timeSec * 1.2 + Math.PI) * bobAmp;
    const localNameX = -bigSize / 2 + 35 + bigSize / 2 + 20;
    const localBubbleY = canvasH - bigSize * 1.0 + bobL - 18;
    const opNameX = CANVAS_WIDTH - (-bigSize / 2 + 35) - bigSize + bigSize / 2 - 20;
    const opBubbleY = canvasH - bigSize * 1.0 + bobR - 18;

    const drawRematchBubble = (cx: number, cy: number, label: string, color: string): void => {
      const bw = ctx.measureText(label).width + 14;
      const bh = 16;
      const bx = cx - bw / 2;
      const by = cy - bh;
      roundedRectPath(ctx, bx, by, bw, bh, 4);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.strokeStyle = "rgba(255,255,255,0.4)";
      ctx.lineWidth = 1;
      ctx.stroke();
      ctx.fillStyle = "#fff";
      ctx.font = mFont("bold", 4.5);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(label, cx, by + bh / 2);
    };

    // Local rematch indicator
    if (model.rematchLocalWants && stepT >= 0.3) {
      const bubbleAlpha = clamp01((stepT - 0.3) / 0.3);
      ctx.globalAlpha = bubbleAlpha;
      drawRematchBubble(localNameX, localBubbleY, "REMATCH!", "rgba(68,255,136,0.85)");
      ctx.globalAlpha = 1;
    }

    // Opponent rematch indicator
    if (model.rematchRemoteWants && !model.rematchRemoteLeft) {
      ctx.globalAlpha = 1;
      drawRematchBubble(opNameX, opBubbleY, "REMATCH!", "rgba(108,200,255,0.85)");
      ctx.globalAlpha = 1;
    }

    // "STARTING..." text when both want rematch
    if (model.rematchLocalWants && model.rematchRemoteWants && !model.rematchRemoteLeft) {
      const pulseT = (Math.sin(timeSec * 4) + 1) / 2;
      ctx.globalAlpha = 0.7 + 0.3 * pulseT;
      ctx.font = mFont("bold", 10);
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillStyle = "#ffd700";
      ctx.shadowColor = "#ffd700";
      ctx.shadowBlur = 12;
      ctx.fillText("STARTING...", centerX, canvasH / 2 + 40);
      ctx.shadowBlur = 0;
      ctx.globalAlpha = 1;
    }

  }

  // REMATCH + EXIT buttons
  const btnDelay = 0.6;
  const dynamicBtnY = panelY + panelH + 14;
  const versusRematch: Rect = { x: menuCols.leftX, y: dynamicBtnY, w: menuCols.halfW, h: menuBtnH() };
  const versusExit: Rect = { x: menuCols.rightX, y: dynamicBtnY, w: menuCols.halfW, h: menuBtnH() };
  if (stepT >= btnDelay) {
    ctx.globalAlpha = clamp01((stepT - btnDelay) / 0.3);
    const btnSlideX = 40 * (1 - easeOutCubic(clamp01((stepT - btnDelay) / 0.4)));

    // Rematch button: changes label based on state
    const rematchLabel = model.rematchRemoteLeft ? "REMATCH" : model.rematchLocalWants ? "WAITING..." : "REMATCH";
    const rematchTint = model.rematchRemoteLeft ? "rgba(60,40,40,0.85)" : model.rematchLocalWants ? "rgba(40,60,80,0.85)" : undefined;
    ctx.save(); ctx.translate(-btnSlideX, 0); drawLobbyBtn(versusRematch, rematchLabel, rematchTint); ctx.restore();
    ctx.save(); ctx.translate(btnSlideX, 0); drawLobbyBtn(versusExit, "EXIT"); ctx.restore();
    ctx.globalAlpha = 1;
  }

  return { versusRematch, versusExit };
}
